import React, { useState, useEffect } from "react";

// API Services
import { getScopesByWeek } from "../../../services/api/dispatch";

// React Icons
import { PiArrowBendDownRightLight } from "react-icons/pi";
import { Trash } from "../../../components/icons/svg/outline";
import { HiPlus } from "react-icons/hi";

// Components
import ScopeInformation from "./ScopeInformation";
import WorkDescriptionInformation from "./WorkDescriptionInformation";
import { usePayoutWorkDescriptionHover } from "./usePayoutWorkDescriptionHover";
import TimeColumn from "./components/TimeColumn";
import JobColumn from "./components/JobColumn";
import HoursColumn from "./components/HoursColumn";
import TotalColumn from "./components/TotalColumn";

// Utils for time formatting and calculations
import {
  formatDate,
  getWeekStart,
  getWeekOfDates,
  getWeekEnd,
  getDateString,
} from "../../../utils/datetime";
import { calculateTotal, isReopenedPayoutReview } from "../../../utils";
import { groupScopesByDay, mapScopesByDate } from "../../../utils/business";
import { validateOverriddenHours } from "../../../utils/timekeeping/shiftCalculations";

/**
 * Checks whether a shift was added during payout review.
 *
 * @param {Object} shift - Payout timesheet shift row.
 * @returns {boolean} True when the row should not count toward worker hours.
 */
const isPayoutCreatedShift = (shift) =>
  shift.isPayoutCreatedShift || shift.id === null || shift.id === undefined;

/**
 * Helper function to calculate original worker hours totals for comparison
 *
 * @param {Array<Object>} shiftsData - Payout timesheet day rows.
 * @param {string} field - Worker hour field to total.
 * @returns {number} Worker-created shift hours.
 */
const calculateWorkerTotal = (shiftsData, field) => {
  if (!shiftsData || !Array.isArray(shiftsData)) return 0;

  return shiftsData.reduce((total, dayData) => {
    const dayTotal = dayData.shifts.reduce((daySum, shift) => {
      if (isPayoutCreatedShift(shift)) return daySum;

      let value = 0;
      if (field === "regularHours") {
        value = Number(shift.regularTime) || 0;
      } else if (field === "overtimeHours") {
        value = Number(shift.overtime) || 0;
      } else if (field === "doubleTimeHours") {
        value = Number(shift.doubleTime) || 0;
      } else if (field === "totalHours") {
        value =
          (Number(shift.regularTime) || 0) + (Number(shift.overtime) || 0) + (Number(shift.doubleTime) || 0);
      }
      return daySum + value;
    }, 0);
    return total + dayTotal;
  }, 0);
};

/**
 * Calculates manager hours log totals for footer tooltip comparisons.
 *
 * @param {Array<Object>} shiftsData - Payout timesheet day rows.
 * @param {string} field - Manager hour field to total.
 * @returns {number} Manager hours total.
 */
const calculateManagerTotal = (shiftsData, field) => {
  if (!shiftsData || !Array.isArray(shiftsData)) return 0;

  return shiftsData.reduce((total, dayData) => {
    const dayTotal = dayData.shifts.reduce((daySum, shift) => {
      let value = 0;
      if (field === "regularHours") {
        value = Number(shift.regularTimeHoursLog) || 0;
      } else if (field === "overtimeHours") {
        value = Number(shift.overtimeHoursLog) || 0;
      } else if (field === "doubleTimeHours") {
        value = Number(shift.doubleTimeHoursLog) || 0;
      } else if (field === "totalHours") {
        value =
          (Number(shift.regularTimeHoursLog) || 0) +
          (Number(shift.overtimeHoursLog) || 0) +
          (Number(shift.doubleTimeHoursLog) || 0);
      }
      return daySum + value;
    }, 0);
    return total + dayTotal;
  }, 0);
};

/**
 * Simplified Shifts component for rendering combined shift data with conflict detection
 *
 * @param {Object} props Component props.
 * @param {Object} props.worker Worker for scope hover details.
 * @param {string} props.weekEnding Saturday week-ending date.
 * @param {string} props.reviewStatus Payout review status (reopened vs not).
 * @param {boolean} props.isEditing Whether rows are editable.
 * @param {Function} props.setIsEditing Editing-mode setter.
 * @param {Array<Object>} props.shiftsToRender Day rows with nested shifts.
 * @param {Function} props.setShiftsToRender Day-rows setter.
 * @param {Function} props.onAddShift Add-shift handler.
 * @param {Function} props.onRemoveShift Remove-shift handler.
 * @param {string} [props.className] Extra wrapper classes.
 * @returns {JSX.Element}
 */
const PayoutShifts = ({
  worker,
  weekEnding,
  reviewStatus,
  isEditing,
  setIsEditing,
  shiftsToRender,
  setShiftsToRender,
  onAddShift,
  onRemoveShift,
  className,
}) => {
  const [scopesByDate, setScopesByDate] = useState([]);
  const isReopenedReview = isReopenedPayoutReview(reviewStatus);
  const translatedHoverByDescription = usePayoutWorkDescriptionHover(shiftsToRender);

  // Check if overridden hours match calculated hours
  const hoursMatch = validateOverriddenHours(shiftsToRender);

  // Get dispatch scopes from the API
  useEffect(() => {
    /**
     * Load dispatch scopes for the week ending Saturday and map them by date.
     *
     * @returns {Promise<void>}
     */
    const fetchDispatchScopes = async () => {
      if (!weekEnding) return;

      try {
        // Convert weekEnding (Saturday) to weekStart (Sunday) using utility function
        const weekEndingDate = new Date(weekEnding);
        const weekStartDate = getWeekStart(weekEndingDate);
        const weekEndDate = getWeekEnd(weekEndingDate);

        const data = await getScopesByWeek(weekStartDate);

        // Create dates array for the week and map scopes to each date
        const dates = getWeekOfDates(weekStartDate, weekEndDate);
        const mappedScopes = mapScopesByDate(dates, groupScopesByDay(data));
        setScopesByDate(mappedScopes);
      } catch (error) {
        console.error("Error fetching dispatch scopes:", error);
      }
    };

    fetchDispatchScopes();
  }, [weekEnding]);

  // If no data is available, show loading or empty state
  if (!shiftsToRender) {
    return (
      <div className="w-full p-4 text-center text-gray-500">
        Loading shift data...
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`grid grid-cols-22 gap-0 w-full border-2 rounded-lg box-border ${isEditing ? "border-gray-200" : "border-gray-200"
          }`}
      >
        {/* Title Row */}
        <div className="col-[span_14_/_span_14] bg-gray-300 py-1 px-1 font-bold border-gray-400 border-b-2 flex items-center justify-center text-base">
          Work Details
        </div>
        <div className="col-span-8 bg-gray-300 py-1 px-1 font-bold border-l-2 border-b-2 border-gray-400 flex items-center justify-center text-base">
          Hours
        </div>

        {/* Header Row */}
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-gray-400 flex items-center justify-center text-sm">
          Day
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          Job
        </div>
        <div className="col-span-4 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          In
        </div>
        <div className="col-span-4 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          Out
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          Info
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l-[2px] border-l-gray-400 border-gray-400 flex items-center justify-center text-sm">
          RT
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          OT
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          DT
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          Total
        </div>

        {/* Data Rows */}
        {shiftsToRender.map((dayData, dayIndex) => {
          const isEvenDay = dayIndex % 2 === 0;
          const bgColor = isEvenDay ? "bg-gray-100" : "bg-gray-200";
          const scopesData = scopesByDate
            ? scopesByDate.find((value) => {
              // value[0] is a Date object, convert to YYYY-MM-DD string in local timezone
              const dateString = getDateString(value[0]);
              return dateString === dayData.date;
            })
            : null;
          var scopesToday = scopesData ? scopesData[1] : null; // [1] as [0] is the date, [1] is the scopes array
          if (scopesToday?.length === 0 && scopesData) {
            scopesToday = "No scopes found";
          } // Scopes fetched but none found for this date

          return (
            <React.Fragment key={`day-${dayIndex}`}>
              {dayData.shifts.length === 0 ? (
                // Empty day row with plus button on hover over day cell
                <>
                  <div
                    className={`col-span-2 ${bgColor} py-3 px-1 border-b border-gray-300 flex flex-col md:flex-row items-center justify-center text-sm h-12 gap-0 md:gap-1 relative group ${isEditing ? 'hover:bg-gray-300 transition-colors cursor-pointer' : ''}`}
                  >
                    <div className="text-center">{dayData.day.substring(0, 3)}</div>
                    <div className="text-center">{formatDate(dayData.date, "MM/DD")}</div>
                    {/* Plus icon - shows on hover when editing */}
                    {isEditing && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none bg-green-50 bg-opacity-80 rounded">
                        <div
                          className="w-full h-full flex items-center justify-center cursor-pointer pointer-events-auto hover:bg-green-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddShift(dayIndex, -1);
                          }}
                          title="Add shift to day"
                        >
                          <HiPlus className="w-4 h-4 text-green-700" />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Combined job, in, out, information cols - empty gray box */}
                  <div
                    className={`col-span-12 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12`}
                  >
                  </div>
                  {/* Combined RT, OT, DT, Total cols - empty gray box */}
                  <div
                    className={`col-span-8 ${bgColor} py-3 px-1 border-b border-l-[2px] border-l-gray-400 border-gray-300 text-sm h-12`}
                  >
                  </div>
                </>
              ) : (
                dayData.shifts.map((shift, shiftIndex) => {
                  return (
                    <React.Fragment key={`${dayIndex}-${shiftIndex}`}>
                      <div
                        className={`col-span-2 ${bgColor} py-3 px-1 border-b border-gray-300 flex flex-col md:flex-row items-center justify-center text-sm h-12 gap-0 md:gap-1 relative group ${isEditing ? 'hover:bg-gray-300 transition-colors' : ''
                          }`}
                      >
                        {shiftIndex === 0 ? (
                          <>
                            <div className="text-center">{dayData.day.substring(0, 3)}</div>
                            <div className="text-center">{formatDate(dayData.date, "MM/DD")}</div>
                          </>
                        ) : (
                          <PiArrowBendDownRightLight className="w-4 h-4 text-gray-500" />
                        )}
                        {/* Plus and Trash icons - shows on hover when editing */}
                        {isEditing && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none rounded overflow-hidden">
                            {/* Plus icon with green background - covers left half */}
                            <div
                              className="w-1/2 h-full flex items-center justify-center bg-green-50 cursor-pointer pointer-events-auto hover:bg-green-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddShift(dayIndex, shiftIndex);
                              }}
                              title="Add shift to day"
                            >
                              <HiPlus className="w-4 h-4 text-green-700" />
                            </div>
                            {/* Trash icon with red background - covers right half */}
                            <div
                              className="w-1/2 h-full flex items-center justify-center bg-red-50 cursor-pointer pointer-events-auto hover:bg-red-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveShift(dayIndex, shiftIndex);
                              }}
                              title="Remove shift"
                            >
                              <Trash className="w-4 h-4 text-red-700" />
                            </div>
                          </div>
                        )}
                      </div>
                      <JobColumn
                        shift={shift}
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                        date={dayData.date}
                      />
                      {/* Start Time */}
                      <TimeColumn
                        shift={shift}
                        timeType="start"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                      />
                      {/* Finish Time */}
                      <TimeColumn
                        shift={shift}
                        timeType="finish"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                      />
                      {/* Scope and work-description information */}
                      <div
                        className={`col-span-2 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12 gap-2`}
                      >
                        <WorkDescriptionInformation
                          workDescription={shift.workDescription}
                          shiftId={shift.id}
                          translatedWorkDescription={
                            translatedHoverByDescription[
                              (shift.workDescription || "").trim()
                            ]
                          }
                        />
                        <ScopeInformation
                          scopes={scopesToday}
                          worker={worker}
                          date={dayData.date}
                          jobNum={shift.jobNum}
                          shiftId={shift.id}
                        />
                      </div>
                      <HoursColumn
                        shift={shift}
                        hoursType="regular"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                      />
                      <HoursColumn
                        shift={shift}
                        hoursType="overtime"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                      />
                      <HoursColumn
                        shift={shift}
                        hoursType="doubletime"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        dayIndex={dayIndex}
                        shiftIndex={shiftIndex}
                        setShiftsToRender={setShiftsToRender}
                      />
                      <HoursColumn
                        shift={shift}
                        hoursType="total"
                        isEditing={isEditing}
                        isReopenedReview={isReopenedReview}
                        bgColor={bgColor}
                        fontWeight="text-sm font-medium"
                        hoursMatch={hoursMatch}
                      />
                    </React.Fragment>
                  );
                })
              )}
              {/* Blank row separator between days (except after the last day) */}
              {dayIndex < shiftsToRender.length - 1 && (
                <React.Fragment>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-4"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-4"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 border-l-2 border-l-gray-400 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                </React.Fragment>
              )}
            </React.Fragment>
          );
        })}

        {/* Final Row with Edit Button and Totals */}
        <div className="col-span-2 bg-gray-300 border-t-2 border-r-2 border-gray-400"></div>
        <div
          className={`col-span-10 ease-in-out transition-colors border-t-2 rounded-b border-gray-400 border-t-gray-400 flex items-center justify-start h-12 ${isEditing ? "bg-yellow-200" : "bg-green-200"
            }`}
        >
          <button
            className={`bg-transparent border-transparent font-bold px-4 rounded-b transition-colors duration-200 w-full focus:outline-none focus:ring-0 h-full ${isEditing && !hoursMatch ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => {
              if (isEditing && !hoursMatch) {
                return; // Prevent confirmation if hours don't match
              }
              setIsEditing(!isEditing);
            }}
            disabled={isEditing && !hoursMatch}
            title={isEditing && !hoursMatch ? "Overridden hours must match calculated hours from clock times" : ""}
          >
            {isEditing ? "Confirm Payout" : "Edit Payout"}
          </button>
        </div>
        <div className="col-span-2 bg-gray-300 border-t-2 border-l-2 border-gray-400"></div>
        <TotalColumn
          payoutTotal={calculateTotal(shiftsToRender, "regularHoursPayout")}
          workerTotal={calculateWorkerTotal(shiftsToRender, "regularHours")}
          managerTotal={calculateManagerTotal(shiftsToRender, "regularHours")}
          hoursType="regular"
        />
        <TotalColumn
          payoutTotal={calculateTotal(shiftsToRender, "overtimeHoursPayout")}
          workerTotal={calculateWorkerTotal(shiftsToRender, "overtimeHours")}
          managerTotal={calculateManagerTotal(shiftsToRender, "overtimeHours")}
          hoursType="overtime"
        />
        <TotalColumn
          payoutTotal={calculateTotal(shiftsToRender, "doubleTimeHoursPayout")}
          workerTotal={calculateWorkerTotal(shiftsToRender, "doubleTimeHours")}
          managerTotal={calculateManagerTotal(shiftsToRender, "doubleTimeHours")}
          hoursType="doubletime"
        />
        <TotalColumn
          payoutTotal={calculateTotal(shiftsToRender, "totalHoursPayout")}
          workerTotal={calculateWorkerTotal(shiftsToRender, "totalHours")}
          managerTotal={calculateManagerTotal(shiftsToRender, "totalHours")}
          hoursType="total"
          hoursMatch={hoursMatch}
        />
      </div>
    </div>
  );
};

export default PayoutShifts;
